import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../components/layoutConfig/layout";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "animate.css";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import DataTable, { TableColumn } from "react-data-table-component";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

/* ---------------------- Tipos ---------------------- */
interface PaymentHistory {
  id: number;
  payment_type: string;
  total_paid: string; // vem como string (ex: "490" que representa 4,90)
  payment_status: string;
  pagseguro_code: string;
  created_at: string;
  updated_at: string;
}

/* ---------------------- Componente ---------------------- */
export default function HistoryPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [payments, setPayments] = useState<PaymentHistory[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  /* ---------------------- Utils ---------------------- */

  const formatDateBR = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  /**
   * Converte o campo recebido em string para número em reais.
   * Regra:
   *  - se a string contém '.' ou ',' -> assume que já está em reais (ex: "4.90" ou "4,90")
   *  - caso contrário -> assume que é valor em centavos (ex: "490" => 4.90)
   */
  const formatCurrency = (value: string) => {
    if (value === null || value === undefined || value === "") {
      return (0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    }

    const raw = String(value).trim();

    let amountInReais = 0;

    if (raw.includes(".") || raw.includes(",")) {
      // já em reais (com separador decimal)
      const normalized = raw.replace(",", ".");
      const parsed = parseFloat(normalized);
      amountInReais = isNaN(parsed) ? 0 : parsed;
    } else {
      // aparentemente veio como centavos (ex: "490" -> 4.90)
      const parsedInt = parseInt(raw, 10);
      amountInReais = isNaN(parsedInt) ? 0 : parsedInt / 100;
    }

    return amountInReais.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.info("Código copiado!");
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      PAID: "success",
      PENDING: "warning",
      WARNING: "secondary",
      CANCELLED: "danger",
    };
    const label: Record<string, string> = {
      PAID: "Pago",
      PENDING: "Pendente",
      WARNING: "Aguardando",
      CANCELLED: "Cancelado",
    };
    return (
      <span className={`badge bg-${map[status] || "secondary"} px-3 py-2`}>
        {label[status] || status}
      </span>
    );
  };

  /* ---------------------- Fetch ---------------------- */
  const handleGetPayments = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.post(`/management/get/historyPayment`);
      const payload = response.data?.success ?? [];
      setPayments(payload);
      setFilteredPayments(payload);
    } catch (err) {
      console.error("Erro ao buscar pagamentos:", err);
      toast.error("❌ Erro ao carregar histórico de pagamentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetPayments();
  }, []);

  /* ---------------------- Filtros ---------------------- */
  useEffect(() => {
    let filtered = [...payments];
    if (filterStatus) {
      filtered = filtered.filter((p) => p.payment_status === filterStatus);
    }
    if (filterType) {
      filtered = filtered.filter(
        (p) => p.payment_type.toLowerCase() === filterType.toLowerCase()
      );
    }
    setFilteredPayments(filtered);
  }, [filterStatus, filterType, payments]);

  /* ---------------------- Colunas da Tabela ---------------------- */
  const columns: TableColumn<PaymentHistory>[] = useMemo(() => {
    const renderActions = (row: PaymentHistory) => (
      <OverlayTrigger placement="top" overlay={<Tooltip>Copiar código PagSeguro</Tooltip>}>
        <button
          className="btn btn-sm btn-outline-secondary rounded-pill"
          onClick={() => copyToClipboard(row.pagseguro_code)}
        >
          <i className="bi bi-clipboard"></i>
        </button>
      </OverlayTrigger>
    );

    return [
      {
        name: "ID",
        selector: (row) => row.id,
        sortable: true,
        width: "70px",
        center: true,
      },
      {
        name: "Tipo de Pagamento",
        selector: (row) => row.payment_type,
        sortable: true,
        width: "150px",
        cell: (row) => (
          <span className="fw-semibold text-primary">
            <i className="bi bi-cash-coin me-2"></i>
            {row.payment_type}
          </span>
        ),
      },
      {
        name: "Valor Pago",
        selector: (row) => row.total_paid,
        sortable: true,
        width: "150px",
        cell: (row) => <span className="fw-bold text-success">{formatCurrency(row.total_paid)}</span>,
      },
      {
        name: "Status",
        selector: (row) => row.payment_status,
        sortable: true,
        width: "150px",
        center: true,
        cell: (row) => getStatusBadge(row.payment_status),
      },
      {
        name: "Código PagSeguro",
        selector: (row) => row.pagseguro_code,
        wrap: true,
        cell: (row) => (
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small text-break">{row.pagseguro_code}</span>
            {renderActions(row)}
          </div>
        ),
      },
      {
        name: "Data",
        selector: (row) => row.created_at,
        sortable: true,
        width: "200px",
        cell: (row) => formatDateBR(row.created_at),
      },
    ];
  }, []);

  const customStyles = {
    headCells: {
      style: { fontWeight: "700", fontSize: "14px", backgroundColor: "#f8f9fa" },
    },
    rows: {
      highlightOnHoverStyle: { backgroundColor: "#f0f8ff", transition: "0.15s" },
    },
    cells: { style: { fontSize: "13px" } },
  };

  /* ---------------------- Render ---------------------- */
  return (
    <Layout>
      <div className="container-fluid py-4 animate__animated animate__fadeIn">
        <h3 className="mb-4 fw-bold text-success">
          <i className="bi bi-clock-history me-2"></i> Histórico de Pagamentos
        </h3>

        {/* Filtros */}
        <div className="row mb-4 g-2 animate__animated animate__fadeInUp">
          <div className="col-md-3">
            <select
              className="form-select shadow-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Tipo de Pagamento</option>
              <option value="PIX">PIX</option>
              <option value="CREDIT_CARD">Cartão</option>
              <option value="BOLETO">Boleto</option>
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select shadow-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Status</option>
              <option value="PAID">Pago</option>
              <option value="PENDING">Pendente</option>
              <option value="WARNING">Aguardando</option>
              <option value="CANCELLED">Cancelado</option>
            </select>
          </div>
        </div>

        {/* Tabela */}
        <div className="card shadow-lg border-0 rounded-4 animate__animated animate__fadeInUp">
          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredPayments}
              pagination
              highlightOnHover
              progressPending={loading}
              responsive
              fixedHeader
              fixedHeaderScrollHeight="60vh"
              noHeader
              customStyles={customStyles}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}
